<script setup lang="ts">
import CourierRequestDialog from "./courier-request/CourierRequestDialog.vue";
import { Card, CardContent } from "@/components/ui/card";
import { formatDate } from "@lib/utils/formatDate";
import { estimatedTimeText1 } from "@lib/utils/estimatedTimeText";
import MapEmbed from "./MapEmbed.vue";
import {
    feeBreakdownNameMap
} from "@lib/utils/calculateCartBreakdown";
import {
    Clock, Info, User, CreditCard,
    Banknote, Calculator, WalletCards, ShoppingBag,
    Gift,
} from "lucide-vue-next";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { formatCurrency } from "@lib/utils/formatCurrency";
import { MapPin } from 'lucide-vue-next'
import DropoffIcon from "@/ui-plus/dropoff-options/DropoffIcon.vue";
import UberDirectList from './uber-direct/UberDirectList.vue';
import DeliveryteamList from './deliveryteam/DeliveryteamList.vue';
import { type storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
type OrderDetailsType = ResType<typeof storeOrdersApiClient[":storeId"]['get_by_order_ids']['$post']>["data"][number];

const paymentIcons = [
    { name: 'CreditCard', component: CreditCard },
    { name: 'Banknote', component: Banknote },
    { name: 'Calculator', component: Calculator },
]

const props = defineProps<{
    orderDetails: OrderDetailsType;
}>();


const orderedQuantity = () => {
    return props.orderDetails?.orderedItemsJSON?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
};


</script>
<template>
    <div class="mx-auto min-w-0 w-full max-w-full">
        <Card class="my-4">
            <CardContent class="p-4">
                <h2 class="text-xl font-bold m-1">Order #{{ orderDetails.shortOtp }}</h2>
                <div class="m-1 text-md font-medium">
                    Status: <span
                          class="capitalize font-bold border border-muted-foreground rounded-md px-2 py-1">{{ orderDetails.orderStatus.toLowerCase() }}</span>
                    <span v-if="orderDetails.isCancelled"
                          class="text-xs border border-destructive-foreground p-1 rounded-md inline-block m-1 bg-destructive text-destructive-foreground underline">
                        Order CANCELLED
                    </span>
                </div>
                <span class="text-xs border border-foreground p-1 rounded-md inline-block m-1">
                    MarketingChannel: {{ orderDetails.websiteDefaultHostname }}
                </span>
                <div class="text-sm text-muted-foreground m-1">
                    <Clock class="inline-block mr-1 h-4 w-4" />
                    Placed on {{ formatDate(orderDetails.order_placed_at_ts, undefined, true) }}
                    <div v-if="orderDetails.order_placed_at_ts !== orderDetails.draft_create_at_ts">
                        Draft: {{ formatDate(orderDetails.draft_create_at_ts, undefined, true) }}
                    </div>
                </div>
                <div v-if="orderDetails.scheduled_at_ts">
                    <span
                          class="text-xs border border-primary-foreground p-1 rounded-md inline-block m-1 bg-primary text-primary-foreground font-bold">
                        SCHEDULED
                    </span>
                    {{ formatDate(orderDetails.scheduled_at_ts!, undefined, true) }}
                </div>
                <div v-if="orderDetails.estimatedTimeJSON"
                     class="text-sm text-muted-foreground m-1">
                    <Clock class="inline-block mr-1 h-4 w-4" />
                    {{ estimatedTimeText1(orderDetails.orderType!, orderDetails.estimatedTimeJSON) }}
                </div>
            </CardContent>
        </Card>

        <!-- Order Notes -->
        <Card class="my-4 min-w-0 max-w-full custom-pulse"
              v-if="orderDetails.orderNote">
            <CardContent class="min-w-0 p-4">
                <h3 class="font-bold text-xl mb-2">Order Notes</h3>
                <div class="min-w-0 max-w-full whitespace-pre-wrap text-sm leading-relaxed [overflow-wrap:anywhere]">
                    {{ orderDetails.orderNote }}
                </div>
            </CardContent>
        </Card>

        <!-- Delivery or Pickup Info -->
        <Card class="my-4"
              v-if="orderDetails.orderType === 'delivery' && orderDetails.deliveryAddressJSON">
            <CardContent class="p-4">
                <h3 class="font-bold text-xl mb-2">Delivery Details</h3>
                <div class="flex items-start gap-2">
                    <div v-if="orderDetails.deliveryAddressJSON.instructionCategory"
                         class="flex flex-col items-center gap-2 text-sm">
                        <DropoffIcon :option="orderDetails.deliveryAddressJSON.instructionCategory" />
                    </div>
                    <MapPin class="mt-1 flex-shrink-0" />
                    <div>
                        <h3 class="font-medium">{{ orderDetails.deliveryAddressJSON.shippingName }}</h3>
                        <p class="text-sm font-bold">{{ orderDetails.deliveryAddressJSON.address1 }}</p>
                        <p v-if="orderDetails.deliveryAddressJSON.address2"
                           class="text-sm font-bold">
                            {{ orderDetails.deliveryAddressJSON.address2 }}
                        </p>
                        <p class="text-sm font-bold">
                            {{ [orderDetails.deliveryAddressJSON.city, orderDetails.deliveryAddressJSON.state].filter(Boolean).join(', ') }}
                            {{ orderDetails.deliveryAddressJSON.postalCode }}
                        </p>
                        <p v-if="orderDetails.deliveryAddressJSON.instructionsForPickupDelivery"
                           class="text-sm italic mt-1">
                            {{ orderDetails.deliveryAddressJSON.instructionsForPickupDelivery }}
                        </p>
                        <p v-if="orderDetails.deliveryAddressJSON.instructionCategory"
                           class="text-sm italic mt-1">
                            {{ orderDetails.deliveryAddressJSON.instructionCategory }}
                        </p>
                        <a v-if="orderDetails.deliveryAddressJSON.phoneNumber"
                           :href="`tel:${orderDetails.deliveryAddressJSON.phoneNumber}`"
                           class="text-sm font-bold">
                            ☎️ {{ orderDetails.deliveryAddressJSON.phoneNumber }}
                        </a>
                    </div>
                </div>
                <div class="text-sm text-muted-foreground m-1">
                    Order count: {{ orderDetails.orderCount }}
                </div>

                <CourierRequestDialog :order="orderDetails" />

                <UberDirectList :order="orderDetails" />

                <DeliveryteamList :order="orderDetails" />

            </CardContent>
        </Card>

        <!-- {{ orderDetails.userVerifiedPhoneNumbers }} -->

        <Card class="my-4"
              v-else-if="orderDetails.orderType === 'pickup'">
            <CardContent class="p-4">
                <h3 class="font-bold text-xl mb-2">Pickup Details</h3>
                <div class="flex items-start gap-2">
                    <User class="mt-1 flex-shrink-0" />
                    <div>
                        <h3 class="font-medium">Pickup Name: {{ orderDetails.pickupNameSurname }}</h3>
                        <p class="text-sm mt-1">Please provide this name when collecting your order.</p>
                    </div>
                </div>
                <div class="text-sm text-muted-foreground m-1">
                    Order count: {{ orderDetails.orderCount }}
                </div>
            </CardContent>
        </Card>



        <!-- Payment Information -->
        <Card class="my-4">
            <CardContent class="p-4">
                <h3 class="font-bold text-xl mb-2">
                    Payment
                    <span v-if="orderDetails?.isCancelled && orderDetails.paymentMethodJSON?.isOnline"
                          class="text-xs border border-destructive-foreground p-1 rounded-md inline-block m-1 bg-destructive text-destructive-foreground underline">
                        REFUND APPLIED
                    </span>
                </h3>
                <div class="flex items-center gap-2">
                    <component :is="paymentIcons.find(icon => icon.name === orderDetails?.paymentMethodJSON?.icon)?.component || WalletCards"
                               class="mr-2 flex-shrink-0" />
                    <div>
                        <p class="font-medium capitalize">{{ orderDetails?.paymentMethodJSON?.name }}</p>
                        <p class="text-sm">{{ orderDetails?.paymentMethodJSON?.description }}</p>
                        <p class="text-sm">
                            {{ orderDetails?.paymentMethodJSON?.isOnline ? 'Online-payment' : 'In-person-payment' }}
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>

        <!-- Order Items -->
        <div class="w-full p-4 rounded-lg border bg-card text-card-foreground shadow-sm my-4">
            <div class="flex justify-between items-center cursor-pointer">

                <h3 class="font-bold text-xl mb-4 flex items-center">
                    <ShoppingBag class="mr-2" />
                    <span class="text-sm text-muted-foreground ml-2">
                        ({{ orderedQuantity() }} items)
                    </span>
                </h3>
            </div>

            <!-- Order Items List -->
            <div v-for="(item, index) in orderDetails.orderedItemsJSON"
                 :key="index"
                 class="border-t border-muted-foreground pt-3 pb-3">
                <div class="whitespace-pre-wrap px-2">
                    <div class="font-bold text-xl">
                        {{ item.name }}
                    </div>
                    <div class="text-sm">{{ item.details }}</div>
                </div>
                <div class="flex justify-between items-center px-1 mt-2">
                    <div>
                        <span class="font-medium">Quantity: {{ item.quantity }}</span>
                    </div>
                    <div>
                        <div class="font-medium">
                            {{ item.shownFee }}
                        </div>
                    </div>
                </div>
                <div v-if="item.hasBuyXGetYFreePromo">
                    <div class="font-bold bg-primary text-primary-foreground p-1 text-xs rounded-md">
                        Buy {{ item.hasBuyXGetYFreePromo.buyQ }}, Get {{ item.hasBuyXGetYFreePromo.getQ }} Free
                        (-{{ formatCurrency(item.hasBuyXGetYFreePromo.discount, orderDetails.taxSettingsJSON?.currency)
                        }}) ({{ item.hasBuyXGetYFreePromo.freeItemsCount }}X)
                    </div>
                </div>
                <div v-if="item.hasPercentagePromo">
                    <div class="font-bold bg-primary text-primary-foreground p-1 text-xs rounded-md">
                        {{ item.hasPercentagePromo.percentage }}% Off
                        (-{{ formatCurrency(item.hasPercentagePromo.discount, orderDetails.taxSettingsJSON?.currency)
                        }})
                    </div>
                </div>
            </div>



            <!-- Subtotal, Delivery, Fees -->
            <div class="border-t border-muted-foreground pt-3 mt-3">

                <div class="flex justify-between items-center py-1"
                     v-if="orderDetails.cartTotalsJSON">
                    <span>Subtotal</span>
                    <span>{{ formatCurrency(orderDetails.cartTotalsJSON.shownFee, orderDetails.taxSettingsJSON?.currency) }}</span>
                </div>

                <div class="flex justify-between items-center py-1"
                     v-if="orderDetails.subtotalJSON?.bestDeliveryZone">
                    <span>Delivery Fee</span>
                    <span>{{ formatCurrency(orderDetails.subtotalJSON.bestDeliveryZone.shownFee, orderDetails.taxSettingsJSON?.currency) }}</span>
                </div>

                <!-- Custom Service Fees -->
                <div v-for="(customFee, index) in orderDetails.subtotalJSON?.calculatedCustomServiceFees"
                     :key="index">
                    <div class="flex justify-between items-center py-1"
                         v-if="customFee.shownFee > 0">
                        <span>{{ customFee.name }}</span>
                        <span>{{ formatCurrency(customFee.shownFee, orderDetails.taxSettingsJSON?.currency) }}</span>
                    </div>
                </div>



                <!-- Tax and Fees -->
                <div class="flex justify-between items-center py-1"
                     v-if="orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.buyerPaysTaxAndFeesShownFee > 0">
                    <span class="flex items-center">
                        {{ orderDetails.cartBreakdownJSON.buyerPaysTaxAndFeesName }}
                        <Popover>
                            <PopoverTrigger as-child>
                                <Info class="inline-block ml-1 h-4 w-4 cursor-pointer" />
                            </PopoverTrigger>
                            <PopoverContent align="start">
                                <div class="space-y-2">
                                    <h3 class="font-semibold">What's included?</h3>
                                    <div v-for="(fee, index) in orderDetails.cartBreakdownJSON.buyerPaysTaxAndFees"
                                         :key="index">
                                        <div class="flex justify-between items-center">
                                            <div class="font-medium">{{ feeBreakdownNameMap[fee.type].name }}</div>
                                            <div>{{ fee.currencyShownFee }}</div>
                                        </div>
                                        <div class="text-sm text-muted-foreground">
                                            {{ feeBreakdownNameMap[fee.type].note }}
                                        </div>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </span>
                    <span>{{ formatCurrency(orderDetails.cartBreakdownJSON.buyerPaysTaxAndFeesShownFee, orderDetails.taxSettingsJSON?.currency) }}</span>
                </div>

                <!-- Discount -->
                <div class="flex justify-between items-center py-1 text-xl font-bold text-primary"
                     v-if="orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.discount > 0">
                    <span>Discount</span>
                    <span>-{{ formatCurrency(orderDetails.cartBreakdownJSON.discount,
                        orderDetails.taxSettingsJSON?.currency) }}</span>
                </div>

                <!-- First Orders Promo -->
                <div class="flex justify-between items-center py-1 text-xl font-bold text-primary"
                     v-if="orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.firstOrdersDiscount > 0">
                    <span>First Orders Promo</span>
                    <span>-{{ formatCurrency(orderDetails.cartBreakdownJSON.firstOrdersDiscount,
                        orderDetails.taxSettingsJSON?.currency) }}</span>
                </div>

                <!-- used store credit -->
                <div class="flex justify-between items-center py-1 text-xl font-bold text-primary"
                     v-if="orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.usedCredit > 0">
                    <span class="">
                        Used Gift Credit
                    </span>
                    <span class=" text-right">
                        -{{ formatCurrency(orderDetails.cartBreakdownJSON.usedCredit,
                            orderDetails.taxSettingsJSON?.currency) }}
                    </span>
                </div>

                <!-- Total -->
                <div
                     class="flex justify-between items-center font-bold text-2xl py-2 border-t border-muted-foreground mt-2">
                    <span>
                        Total
                        <Popover>
                            <PopoverTrigger as-child>
                                <Info class="inline-block" />
                            </PopoverTrigger>
                            <PopoverContent align="start">
                                <div class="space-y-2">
                                    <h3 class="font-semibold">Transparency breakdown</h3>
                                    <div v-if="orderDetails.cartBreakdownJSON?.allTransparencyBreakdown">
                                        <div v-for="(fee, index) in orderDetails.cartBreakdownJSON.allTransparencyBreakdown"
                                             :key="index"
                                             class="my-4">
                                            <div class="flex justify-between items-center">
                                                <div class="font-medium">
                                                    {{ feeBreakdownNameMap[fee.type].name }}
                                                </div>
                                                <div>
                                                    {{ fee.currencyShownFee }}
                                                </div>
                                            </div>
                                            <div class="text-sm text-muted-foreground">
                                                {{ feeBreakdownNameMap[fee.type].note }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </span>
                    <span>{{ formatCurrency(orderDetails.finalAmount, orderDetails.taxSettingsJSON?.currency) }}</span>
                </div>

                <div class="border border-primary p-0 rounded-md text-center text-2xl cursor-pointer"
                     v-if="orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.earnCreditAmountByOrdering > 0">
                    <span class="text-xs text-muted-foreground">
                        Customer gift credit
                    </span>
                    <br>
                    <Gift class="min-w-6 min-h-6 inline-block text-primary" />
                    {{ formatCurrency(orderDetails.cartBreakdownJSON?.earnCreditAmountByOrdering, orderDetails.taxSettingsJSON?.currency) }}
                </div>
            </div>
        </div>
        <div v-if="orderDetails.storeLocationLat && orderDetails.storeLocationLng"
             class="mt-3 overflow-hidden rounded-lg">
            <MapEmbed :storeLat="orderDetails.storeLocationLat"
                      :storeLng="orderDetails.storeLocationLng"
                      :destinationLat="orderDetails.deliveryAddressJSON?.lat"
                      :destinationLng="orderDetails.deliveryAddressJSON?.lng"
                      :isLink="true" />
        </div>
        <details v-if="orderDetails.statusHistory.length > 0">
            <summary class="font-bold text-xl mb-4">Status changes</summary>
            <div v-for="(status, index) in orderDetails.statusHistory"
                 :key="index"
                 class="text-xs text-muted-foreground py-2">
                {{ status.newStatus }}
                <br>
                {{ formatDate(status.created_at_ts, undefined, true) }}
                <br>
                <details>
                    <summary>
                        more
                    </summary>
                    <pre>{{ status }}</pre>
                </details>
            </div>
        </details>
    </div>
</template>
